import React, { memo, useCallback, Fragment, useEffect } from 'react';
import { useObservable } from '@core/utils/hooks/rxjs';
import { Row, Col, Card, CardBody, CardTitle } from 'reactstrap';
import { FormInput } from '@core/components/form';
import { SortableList } from '@modules/common/Sortable';
import { TodoListSectionItem } from './TodoListSectionItem';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { FormHTML } from '@core/components/form/FormHTML';
import { TodoListBlockVariation, TodoListForm, TodoListSectionForm } from './TodoListBlock';

export interface TodoListBlockSettingsProps {
  form: TodoListForm;
  variation: TodoListBlockVariation;
}

export const TodoListBlockSettings = memo((props: TodoListBlockSettingsProps) => {
  const { form, variation } = props;
  const sections = useObservable(form.controls.sections.items$, []);
  const sectionValues = useObservable(form.controls.sections.value$, []);

  useEffect(() => {
    if (sections.length === 0) {
      form.controls.sections.push();
    }
  }, [form, sections]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newSections = R.move(sectionValues, oldIndex, newIndex);
      form.patchValue({
        sections: newSections,
      });
    },
    [form, sectionValues],
  );

  return (
    <div>
      <Fragment>
        <Row>
          <Col md={12}>
            <FormInput control={form.controls.name} />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormHTML autoFocus minHeight={300} control={form.controls.instructions} minimal={true} />
          </Col>
        </Row>
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {sections.map((ctrl, index) => {
            return (
              <Card className="bg-light" key={index}>
                <CardTitle>
                  <span className="text-muted ml-2 mt-2" style={{ opacity: 0.5 }}>
                    Section #{index + 1}
                  </span>
                </CardTitle>
                <CardBody>
                  <SortableTodoListSection key={index} index={index} ctrl={ctrl} form={form} i={index} />
                </CardBody>
              </Card>
            );
          })}
        </SortableList>
      </Fragment>
    </div>
  );
});

interface SortableSectionProps {
  i: number;
  ctrl: TodoListSectionForm;
  form: TodoListForm;
}

const SortableSection = memo((props: SortableSectionProps) => {
  const { ctrl, form, i } = props;

  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical font-size-16 mr-3 mb-3" style={{ cursor: 'move' }}></i>);

  return (
    <div>
      <Row key={`question-${i} mb-2 mt-2`}>
        <Col md={10} className="input-width">
          <DragHandle />
          <FormInput control={ctrl.controls.sectionTitle} />
        </Col>
        <Col md={2}>
          {!i && (
            <a className="btn btn-outline-primary btn-sm mr-2" onClick={() => form.controls.sections.push()}>
              Add Section
            </a>
          )}
          {!!i && (
            <a className="btn btn-outline-danger btn-sm mr-2" onClick={() => form.controls.sections.removeAt(i)}>
              <i className="bx bx-x"></i>
            </a>
          )}
        </Col>
      </Row>
      <TodoListSectionItem control={ctrl} />
    </div>
  );
});

export const SortableTodoListSection = SortableElement(SortableSection);